<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { debounce } from 'lodash-es'
import { useNotification } from '@kyvg/vue3-notification'
import { useScreen } from '@/composables/useScreen'
import { apiPost } from '@/lib/http'
import type { EventCardEvent } from './types'

const modes = ['next', 'previous', 'my'] as const
type Mode = (typeof modes)[number]

const props = defineProps<{
  mode: Mode
}>()

const emit = defineEmits<{
  (e: 'update:mode', value: Mode): void
  (e: 'events-updated', value: EventCardEvent[]): void
  (e: 'fetch-start'): void
  (e: 'fetch-end'): void
  (e: 'error'): void
}>()

const { isMobile } = useScreen()
const { notify } = useNotification()

const order = ref('start_time')
const invertOrder = ref(false)
const likedOnly = ref(false)

const tabValue = computed({
  get: () => modes.indexOf(props.mode),
  set: (value: number) => emit('update:mode', modes[value] ?? 'next')
})

watch(
  () => props.mode,
  value => {
    order.value = value === 'previous' ? '-start_time' : 'start_time'
    fetchEvents()
  }
)

watch([order, invertOrder, likedOnly], () => fetchEvents())

onMounted(() => {
  fetchEvents(true)
})

function fetchEvents(switchOnEmpty = false) {
  emit('events-updated', [])
  emit('fetch-start')
  fetchEventsDelayed(switchOnEmpty)
}

const fetchEventsDelayed = debounce(async (switchOnEmpty: boolean) => {
  const url = props.mode === 'my' ? '/events/request_my_events' : '/events/request_events_showcase'

  const options = {
    order: `${invertOrder.value ? '-' : ''}${order.value}`,
    liked_only: likedOnly.value,
    past: props.mode === 'previous' ? 'true' : ''
  }

  try {
    const resp = await apiPost<EventCardEvent[]>(url, options)
    if (resp.failed) throw resp.message

    if (props.mode === 'next' && !resp.data.length && switchOnEmpty) {
      tabValue.value = 1
      return
    }

    emit('events-updated', resp.data)
  } catch (err) {
    let title = 'Произошла ошибка при получении списка мероприятий.'
    if (typeof err === 'string') title = err
    notify({ type: 'error', title })
    emit('error')
  }

  emit('fetch-end')
}, 300)
</script>

<template>
  <header class="events-filters">
    <v-buttons-group v-model="tabValue" mode="inactive" class="events-filters__tabs">
      <v-button mode="primary block large"> Афиша </v-button>
      <v-button mode="primary block large">
        Прошедшие
        <span v-if="!isMobile">&nbsp;события</span>
      </v-button>
      <v-button v-if="!isMobile" mode="primary block large"> Мои мероприятия </v-button>
    </v-buttons-group>

    <v-link
      v-if="isMobile"
      mode="primary"
      class="text-body-bold"
      @click="
        () => {
          tabValue = 2
        }
      "
    >
      Мои мероприятия
    </v-link>

    <div class="events-filters__filters">
      <!-- <span v-if="!$isMobile">
        Сортировать
      </span>

      <button
        v-for="type in ORDER_TYPES"
        :key="type.value"
        class="events-filters__badge"
        :class="{ 'events-filters__badge_active': order === type.value }"
        @click="onOrderClick(type.value)"
      >
        {{ type.label }}

        <template v-if="order === type.value">
          <v-icon key="desc" v-if="invertOrder">mdi-sort-variant</v-icon>
          <v-icon key="asc" v-else>mdi-sort-reverse-variant</v-icon>
        </template>
      </button>

      <div class="flex-spacer" v-if="$isMobile"></div>-->

      <v-link v-if="isMobile" type="button" title="Избранные мероприятия" @click="() => (likedOnly = !likedOnly)">
        <v-icon v-if="likedOnly" key="off" class="red-text">mdi-heart</v-icon>
        <v-icon v-else key="on" class="gray-text">mdi-heart-outline</v-icon>
      </v-link>

      <v-button
        v-else
        class="events-filters__liked-btn"
        :mode="likedOnly ? 'primary flat small' : 'primary text small'"
        @click="() => (likedOnly = !likedOnly)"
      >
        Избранное
      </v-button>
    </div>

    <!-- <v-button
      v-if="!$isMobile"
      class="events-filters__liked-btn"
      @click="() => likedOnly = !likedOnly"
      :mode="likedOnly ? 'primary flat small' : 'primary text small'"
    >
      Избранное
    </v-button> -->
  </header>
</template>

<style lang="scss">
.events-filters {
  display: grid;
  gap: 24px;

  @include desktop {
    grid-template-columns: 1fr auto;
    align-items: center;
    &__tabs {
      grid-column: 1 / 3;
    }
    &__filters {
      grid-column: 1 / 2;
    }
    &__liked {
      grid-column: 2 / 3;
    }
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__badge {
    cursor: pointer;
    border: 1px solid $gray;
    border-radius: 8px;
    padding: 2px 8px;

    display: flex;
    align-items: center;
    gap: 8px;

    transition:
      border-color 0.1s ease,
      color 0.1s ease,
      background 0.1s ease;

    &_active {
      border-color: var(--color, $blue);
      color: var(--color, $blue);
      background: var(--bg_color, $lightenBlue);
    }
  }

  &__liked-btn._text._primary:focus {
    background-color: unset;
  }
}
</style>
